import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Reveal, Label } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const packages = ["The Garden Room", "The Manor Suite", "Not sure yet"];

const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", package: "", message: "" });
  const [date, setDate] = useState();
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      const message = date
        ? `${form.message}${form.message ? " · " : ""}Preferred arrival: ${format(date, "PPP")}`
        : form.message;
      await axios.post(`${API}/leads`, { ...form, message });
      toast.success("You're on the list. We'll be in touch to confirm your spot for 24–27 September.");
      setForm({ name: "", email: "", phone: "", package: "", message: "" });
      setDate(undefined);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-manor-bg/60 border-white/15 text-manor-text placeholder:text-manor-sage/60 h-14 rounded-none focus-visible:ring-manor-rust focus-visible:border-manor-rust";

  return (
    <section id="reserve" data-testid="reserve-section" className="bg-manor-surface">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-28 md:grid-cols-12 md:px-10 md:py-40">
        <Reveal className="md:col-span-5">
          <Label>Reserve Your Spot</Label>
          <h2 className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tight text-manor-text sm:text-5xl lg:text-6xl">
            24–27 September. Leave your details.
          </h2>
          <p className="mt-8 max-w-sm text-base font-light leading-relaxed text-manor-sage">
            Spots are limited and confirmed personally with each guest. Tell us who you are
            and we&apos;ll hold your place and share travel notes and everything else.
          </p>
          <div className="mt-10 space-y-2 font-mono text-xs tracking-[0.15em] uppercase text-manor-sage">
            <p>The Manor House · Dehradun</p>
            <p>hello@themanorhouse.in</p>
          </div>
        </Reveal>

        <div className="md:col-span-6 md:col-start-7">
          <form onSubmit={submit} data-testid="lead-capture-form" className="space-y-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Input data-testid="lead-name" className={inputCls} placeholder="Full name" value={form.name} onChange={update("name")} />
              <Input data-testid="lead-email" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={update("email")} />
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Input data-testid="lead-phone" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} />
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="lead-date-trigger"
                    className={`flex h-14 items-center justify-between border border-white/15 bg-manor-bg/60 px-4 text-left text-sm transition-colors hover:border-manor-rust ${
                      date ? "text-manor-text" : "text-manor-sage/60"
                    }`}
                  >
                    {date ? format(date, "PPP") : "Preferred arrival"}
                    <CalendarIcon className="h-4 w-4 text-manor-sage" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-white/10 bg-manor-surface p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date()}
                    initialFocus
                    className="text-manor-text"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              {packages.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`lead-package-${p.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, package: p }))}
                  className={`rounded-full border px-5 py-2 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                    form.package === p
                      ? "border-manor-rust bg-manor-rust text-manor-bg"
                      : "border-white/15 text-manor-sage hover:border-white/40"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>

            <Textarea data-testid="lead-message" className={`${inputCls} h-32 resize-none py-4`} placeholder="Anything we should know? (optional)" value={form.message} onChange={update("message")} />

            <button
              type="submit"
              data-testid="lead-submit"
              disabled={loading}
              className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-manor-rust px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-manor-bg transition-colors duration-300 hover:bg-manor-text disabled:opacity-60"
            >
              {loading ? "Sending…" : "Hold my spot"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
